'use client';
import React from 'react';
import AnnouncementBar from '@/components/AnnouncementBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Image from 'next/image';

export default function AboutPage() {
  return (
    <div style={{ backgroundColor: '#EFE3E1', minHeight: '100vh' }}>
      <AnnouncementBar />
      <Header />

      <main>
        {/* ── Page Hero ── */}
        <section
          className="relative w-full overflow-hidden"
          style={{
            backgroundColor: '#1A2332',
            minHeight: 'clamp(280px, 35vw, 480px)',
          }}
        >
          <Image
            src="/assets/images/d892c2b7-79a2-4c99-8af6-c8d6fedeec25-1782133647138.png"
            alt=""
            fill
            sizes="100vw"
            quality={80}
            priority
            className="object-cover"
            style={{ objectPosition: 'center' }}
          />
          <div
            className="absolute inset-0"
            style={{ background: 'rgba(26,35,50,0.65)' }}
          />
          <div
            className="relative z-10"
            style={{
              maxWidth: '1200px',
              margin: '0 auto',
              padding: 'clamp(72px, 9vw, 128px) clamp(24px, 4vw, 64px)',
            }}
          >
            <p
              style={{
                fontFamily: 'adobe-garamond-pro, Georgia, serif',
                fontSize: '11px',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: '#8A9E8C',
                marginBottom: '20px',
              }}
            >
              About the Practice
            </p>
            <h1
              style={{
                fontFamily: 'orpheus-pro, Georgia, serif',
                fontSize: 'clamp(36px, 5vw, 72px)',
                fontWeight: '400',
                color: '#F5F0EA',
                lineHeight: '1.1',
                maxWidth: '640px',
              }}
            >
              Dr Jennifer<br />
              <em style={{ fontStyle: 'normal' }}>Morgan</em>
            </h1>
          </div>
        </section>

        {/* ── Portrait + Intro ── */}
        <section
          className="w-full"
          style={{ backgroundColor: '#EFE3E1', paddingTop: 'clamp(64px, 8vw, 112px)', paddingBottom: 'clamp(48px, 6vw, 80px)' }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 clamp(24px, 4vw, 64px)' }}>
            <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-20">

              {/* Portrait */}
              <div className="w-full lg:w-5/12 flex-shrink-0">
                <div style={{ position: 'relative', width: '100%', aspectRatio: '3 / 4', overflow: 'hidden' }}>
                  <img
                    src="/assets/images/image_2026-06-18_111913028-1781777899598.png"
                    alt="Dr Jennifer Morgan — Chartered Clinical Psychologist"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      objectPosition: 'center top',
                      display: 'block',
                    }}
                  />
                </div>
              </div>

              {/* Intro bio + credentials */}
              <div className="w-full lg:w-7/12">
                <p
                  style={{
                    fontFamily: 'adobe-garamond-pro, Georgia, serif',
                    fontSize: '11px',
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: '#8A9E8C',
                    marginBottom: '16px',
                  }}
                >
                  Qualifications &amp; Background
                </p>

                {/* Credentials strip */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '36px' }}>
                  {['BA Hons', 'MSc', 'DClinPsych (Oxon)', 'DipCAT', 'CPsychol', 'HCPC Registered'].map(cred => (
                    <span
                      key={cred}
                      style={{
                        fontFamily: 'adobe-garamond-pro, Georgia, serif',
                        fontSize: '12px',
                        letterSpacing: '0.06em',
                        color: '#4A4A4A',
                        backgroundColor: '#F5F0EA',
                        padding: '5px 12px',
                        border: '1px solid rgba(26,35,50,0.12)',
                      }}
                    >
                      {cred}
                    </span>
                  ))}
                </div>

                {/* Intro paragraphs */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {[
                    'I work as a Consultant Clinical Psychologist with over 20 years of clinical experience across NHS, independent and private practice settings. I completed my clinical training at the University of Oxford and have since developed a practice focused on providing highly individualised, formulation-led psychological care.',
                    'My work is grounded in the belief that psychological difficulties are best understood within the context of a person\'s life, relationships, history and current circumstances. Rather than applying a fixed model of therapy, I take an integrative and reflective approach, drawing on a range of evidence-based psychological frameworks to develop a clear and meaningful understanding of each individual.',
                    'I work with a limited number of clients in order to maintain depth, continuity and quality of care.',
                  ].map((para, i) => (
                    <p
                      key={i}
                      style={{
                        fontFamily: 'adobe-garamond-pro, Georgia, serif',
                        fontSize: 'clamp(16px, 1.3vw, 19px)',
                        color: '#4A4A4A',
                        lineHeight: '1.8',
                      }}
                    >
                      {para}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Pull Quote ── */}
        <section
          style={{ backgroundColor: '#1A2332', padding: 'clamp(56px, 7vw, 96px) clamp(24px, 4vw, 64px)' }}
        >
          <div style={{ maxWidth: '860px', margin: '0 auto', textAlign: 'center' }}>
            <div
              style={{
                width: '40px',
                height: '2px',
                backgroundColor: '#8A9E8C',
                margin: '0 auto 36px',
              }}
            />
            <p
              style={{
                fontFamily: 'orpheus-pro, Georgia, serif',
                fontSize: 'clamp(22px, 2.8vw, 36px)',
                fontWeight: '400',
                color: '#F5F0EA',
                lineHeight: '1.5',
                fontStyle: 'italic',
              }}
            >
              "Meaningful psychological change arises from understanding, not just intervention."
            </p>
            <div
              style={{
                width: '40px',
                height: '2px',
                backgroundColor: '#8A9E8C',
                margin: '36px auto 0',
              }}
            />
          </div>
        </section>

        {/* ── Clinical Experience + Approach ── */}
        <section
          style={{ backgroundColor: '#F5F0EA', padding: 'clamp(64px, 8vw, 112px) clamp(24px, 4vw, 64px)' }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">

              {/* Left: Clinical Experience */}
              <div className="w-full lg:w-1/2">
                <p
                  style={{
                    fontFamily: 'adobe-garamond-pro, Georgia, serif',
                    fontSize: '11px',
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: '#8A9E8C',
                    marginBottom: '20px',
                  }}
                >
                  Clinical Experience
                </p>
                <div
                  style={{
                    width: '32px',
                    height: '1px',
                    backgroundColor: '#1A2332',
                    marginBottom: '28px',
                    opacity: 0.3,
                  }}
                />
                <p
                  style={{
                    fontFamily: 'adobe-garamond-pro, Georgia, serif',
                    fontSize: 'clamp(16px, 1.3vw, 19px)',
                    color: '#4A4A4A',
                    lineHeight: '1.8',
                  }}
                >
                  Over the course of my career, I have worked with a wide range of complex presentations, including anxiety and mood difficulties, trauma-related conditions, personality-related difficulties, neurodevelopmental presentations, cognitive and neurological concerns, and relational and occupational stress. This breadth of experience informs a nuanced and thoughtful approach to assessment, formulation and intervention.
                </p>
              </div>

              {/* Right: Approach */}
              <div className="w-full lg:w-1/2">
                <p
                  style={{
                    fontFamily: 'adobe-garamond-pro, Georgia, serif',
                    fontSize: '11px',
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: '#8A9E8C',
                    marginBottom: '20px',
                  }}
                >
                  Approach
                </p>
                <div
                  style={{
                    width: '32px',
                    height: '1px',
                    backgroundColor: '#1A2332',
                    marginBottom: '28px',
                    opacity: 0.3,
                  }}
                />
                <p
                  style={{
                    fontFamily: 'adobe-garamond-pro, Georgia, serif',
                    fontSize: 'clamp(16px, 1.3vw, 19px)',
                    color: '#4A4A4A',
                    lineHeight: '1.8',
                  }}
                >
                  My approach is characterised by careful attention to detail, clinical rigour, and a commitment to understanding each person in depth. I aim to provide a therapeutic space that is thoughtful, collaborative and contained, where meaningful psychological change can take place at a pace that feels appropriate.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Additional Services ── */}
        <section
          style={{ backgroundColor: '#EFE3E1', padding: 'clamp(64px, 8vw, 112px) clamp(24px, 4vw, 64px)' }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">

              {/* Text */}
              <div className="w-full lg:w-7/12">
                <p
                  style={{
                    fontFamily: 'adobe-garamond-pro, Georgia, serif',
                    fontSize: '11px',
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: '#8A9E8C',
                    marginBottom: '20px',
                  }}
                >
                  Beyond Individual Therapy
                </p>
                <p
                  style={{
                    fontFamily: 'adobe-garamond-pro, Georgia, serif',
                    fontSize: 'clamp(16px, 1.3vw, 19px)',
                    color: '#4A4A4A',
                    lineHeight: '1.8',
                    marginBottom: '32px',
                  }}
                >
                  Alongside my clinical work, I provide specialist psychological assessments, clinical supervision, and training and consultancy to health professionals and organisations. I also offer a discreet, concierge-style service for individuals seeking highly personalised psychological care delivered with flexibility, continuity and discretion.
                </p>
                <a
                  href="/contact"
                  style={{
                    fontFamily: 'adobe-garamond-pro, Georgia, serif',
                    fontSize: '13px',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: '#EFE3E1',
                    backgroundColor: '#1A2332',
                    padding: '14px 32px',
                    textDecoration: 'none',
                    display: 'inline-block',
                    transition: 'background-color 0.25s ease',
                  }}
                  onMouseEnter={e => ((e.currentTarget as HTMLElement).style.backgroundColor = '#2C2C2C')}
                  onMouseLeave={e => ((e.currentTarget as HTMLElement).style.backgroundColor = '#1A2332')}
                >
                  Arrange a Consultation
                </a>
              </div>

              {/* Stat / accent block */}
              <div
                className="w-full lg:w-5/12 flex-shrink-0"
                style={{
                  backgroundColor: '#1A2332',
                  padding: 'clamp(40px, 5vw, 64px)',
                }}
              >
                <p
                  style={{
                    fontFamily: 'orpheus-pro, Georgia, serif',
                    fontSize: 'clamp(52px, 6vw, 80px)',
                    fontWeight: '400',
                    color: '#F5F0EA',
                    lineHeight: '1',
                    marginBottom: '12px',
                  }}
                >
                  20+
                </p>
                <p
                  style={{
                    fontFamily: 'adobe-garamond-pro, Georgia, serif',
                    fontSize: '13px',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: '#8A9E8C',
                    marginBottom: '28px',
                  }}
                >
                  Years of Clinical Experience
                </p>
                <div style={{ width: '100%', height: '1px', backgroundColor: 'rgba(245,240,234,0.15)', marginBottom: '28px' }} />
                <p
                  style={{
                    fontFamily: 'adobe-garamond-pro, Georgia, serif',
                    fontSize: 'clamp(14px, 1.1vw, 16px)',
                    color: 'rgba(245,240,234,0.7)',
                    lineHeight: '1.7',
                  }}
                >
                  NHS, independent and private practice settings — with clinical training at the University of Oxford.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
